// ======================================================================
// FILE: src/modules/wms/template/pages/TemplateFormPage.js
// MODULE: Work Management System (WMS)
// FEATURE: Template Management
//
// PURPOSE:
//   Create / Edit Template
//   Manage Template Items
//   Single Master Screen
//
// ROUTES:
//   /wms/templates/create
//   /wms/templates/:id/edit
//
// DEPENDENCIES:
//   wmsTemplateService
//   TemplateItemsTab
//
// ARCHITECTURE:
//   ✓ TaskFlow ERP Standard
//   ✓ AdminLTE
//   ✓ Multi Tenant
//   ✓ Multi Location
//   ✓ Audit Enabled
// ======================================================================

import React, {
  useEffect,
  useState,
} from "react";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import wmsTemplateService
from "../../services/wmsTemplateService";

import TemplateItemsTab
from "../components/TemplateItemsTab";

import TemplateChecklistsTab
from "../components/TemplateChecklistsTab";

export default function TemplateFormPage() {

  const navigate = useNavigate();

  const { id } = useParams();

  const isEdit = !!id;

  const [saving, setSaving] =
    useState(false);

  const [loading, setLoading] =
    useState(false);
  
  const [activeTab, setActiveTab] =
    useState("ITEMS");

  const [form, setForm] =
    useState({

      template_code: "",
      template_name: "",

      work_item_type: "TASK",

      category: "",

      //department_id: "",

      priority: "MEDIUM",

      estimated_hours: "",

      approval_required: false,

      auto_assign_team_id: "",

      description: "",
      remarks: "",
    });

  // ==========================================================
  // LOAD
  // ==========================================================

  useEffect(() => {

    if (!isEdit) return;

    loadTemplate();

  }, [id]);

  async function loadTemplate() {

    try {

      setLoading(true);

      const data =
        await wmsTemplateService
          .getTemplate(id);

      setForm({

        template_code:
          data.template_code || "",

        template_name:
          data.template_name || "",

        work_item_type:
          data.work_item_type || "TASK",

        category:
          data.category || "",

        //department_id:          data.department_id || "",

        priority:
          data.priority || "MEDIUM",

        estimated_hours:
          data.estimated_hours || "",

        approval_required:
          data.approval_required || false,

        auto_assign_team_id:
          data.auto_assign_team_id || "",

        description:
          data.description || "",

        remarks:
          data.remarks || "",
      });

    } catch (err) {

      alert(
        err?.response?.data?.message
        || "Failed to load template"
      );

    } finally {

      setLoading(false);
    }
  }

  // ==========================================================
  // CHANGE
  // ==========================================================

  function handleChange(
    field,
    value
  ) {

    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  // ==========================================================
  // VALIDATION
  // ==========================================================

  function validate() {

    if (
      !form.template_code?.trim()
    ) {

      alert(
        "Template Code is required"
      );

      return false;
    }

    if (
      !form.template_name?.trim()
    ) {

      alert(
        "Template Name is required"
      );

      return false;
    }

    if (
      !form.work_item_type
    ) {

      alert(
        "Work Item Type is required"
      );

      return false;
    }

    if (
      !form.priority
    ) {

      alert(
        "Priority is required"
      );

      return false;
    }

    return true;
  }

  // ==========================================================
  // SAVE
  // ==========================================================

  async function handleSave() {

    if (!validate()) {
      return;
    }

    try {

      setSaving(true);

      const payload = {

        template_code:
          form.template_code,

        template_name:
          form.template_name,

        work_item_type:
          form.work_item_type,

        category:
          form.category || null,

        //department_id:           form.department_id || null,

        priority:
          form.priority,

        estimated_hours:
          form.estimated_hours || null,

        approval_required:
          form.approval_required,

        auto_assign_team_id:
          form.auto_assign_team_id || null,

        description:
          form.description,

        remarks:
          form.remarks,
      };

      if (isEdit) {

        await wmsTemplateService
          .updateTemplate(
            id,
            payload
          );

      } else {

        await wmsTemplateService
          .createTemplate(
            payload
          );
      }

      navigate(
        "/wms/templates"
      );

    } catch (err) {

      alert(
        err?.response?.data?.message
        || "Save failed"
      );

    } finally {

      setSaving(false);
    }
  }

  // ==========================================================
  // UI
  // ==========================================================

  if (loading) {

    return (

      <div className="container-fluid">
        Loading...
      </div>

    );
  }

  return (

    <div className="container-fluid">

      {/* ==================================================== */}
      {/* HEADER */}
      {/* ==================================================== */}

      <div
        className="card border-0 mb-3"
        style={{
          background:
            "linear-gradient(90deg,#0d6efd,#4f8cff)",
          color: "#fff",
        }}
      >

        <div className="card-body">

          <h3 className="mb-1">

            {isEdit
              ? "Edit Template"
              : "Create Template"}

          </h3>

          <div>

            Configure reusable work
            templates and checklist items

          </div>

        </div>

      </div>

      {/* ==================================================== */}
      {/* DETAILS */}
      {/* ==================================================== */}

      <div className="card">

        <div className="card-header">

          <strong>
            Template Details
          </strong>

        </div>

        <div className="card-body">

          <div className="row">

            <div className="col-md-3 mb-3">

              <label>
                Template Code *
              </label>

              <input
                className="form-control"
                value={
                  form.template_code
                }
                onChange={(e) =>
                  handleChange(
                    "template_code",
                    e.target.value
                  )
                }
              />

            </div>

            <div className="col-md-5 mb-3">

              <label>
                Template Name *
              </label>

              <input
                className="form-control"
                value={
                  form.template_name
                }
                onChange={(e) =>
                  handleChange(
                    "template_name",
                    e.target.value
                  )
                }
              />

            </div>

            <div className="col-md-2 mb-3">

              <label>
                Work Item Type *
              </label>

              <select
                className="form-control"
                value={
                  form.work_item_type
                }
                onChange={(e) =>
                  handleChange(
                    "work_item_type",
                    e.target.value
                  )
                }
              >

                <option value="TASK">
                  Task
                </option>

                <option value="AUDIT">
                  Audit
                </option>

                <option value="OBSERVATION">
                  Observation
                </option>

                <option value="CAPA">
                  CAPA
                </option>

              </select>

            </div>

            <div className="col-md-2 mb-3">

              <label>
                Priority *
              </label>

              <select
                className="form-control"
                value={
                  form.priority
                }
                onChange={(e) =>
                  handleChange(
                    "priority",
                    e.target.value
                  )
                }
              >

                <option value="LOW">
                  Low
                </option>

                <option value="MEDIUM">
                  Medium
                </option>

                <option value="HIGH">
                  High
                </option>

                <option value="CRITICAL">
                  Critical
                </option>

              </select>

            </div>

            <div className="col-md-3 mb-3">

              <label>
                Category
              </label>

              <input
                className="form-control"
                value={form.category}
                onChange={(e) =>
                  handleChange(
                    "category",
                    e.target.value
                  )
                }
              />

            </div>

            

            <div className="col-md-3 mb-3">

              <label>
                Estimated Hours
              </label>

              <input
                type="number"
                className="form-control"
                value={
                  form.estimated_hours
                }
                onChange={(e) =>
                  handleChange(
                    "estimated_hours",
                    e.target.value
                  )
                }
              />

            </div>

            <div className="col-md-3 mb-3">

              <label>
                Auto Assign Team
              </label>

              <input
                className="form-control"
                value={
                  form.auto_assign_team_id
                }
                onChange={(e) =>
                  handleChange(
                    "auto_assign_team_id",
                    e.target.value
                  )
                }
              />

            </div>

            <div className="col-md-12 mb-3">

              <div className="form-check">

                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={
                    form.approval_required
                  }
                  onChange={(e) =>
                    handleChange(
                      "approval_required",
                      e.target.checked
                    )
                  }
                />

                <label
                  className="form-check-label"
                >
                  Approval Required
                </label>

              </div>

            </div>

            <div className="col-md-12 mb-3">

              <label>
                Description
              </label>

              <textarea
                rows="3"
                className="form-control"
                value={
                  form.description
                }
                onChange={(e) =>
                  handleChange(
                    "description",
                    e.target.value
                  )
                }
              />

            </div>

            <div className="col-md-12 mb-3">

              <label>
                Remarks
              </label>

              <textarea
                rows="3"
                className="form-control"
                value={
                  form.remarks
                }
                onChange={(e) =>
                  handleChange(
                    "remarks",
                    e.target.value
                  )
                }
              />

            </div>

          </div>

        </div>

      </div>

      {/* ==================================================== */}
      {/* TEMPLATE ITEMS */}
      {/* ==================================================== */}

      {isEdit && (

        <div className="card mt-3">

            <div className="card-header">

            <ul className="nav nav-tabs card-header-tabs">

                <li className="nav-item">

                <button
                    type="button"
                    className={
                    activeTab === "ITEMS"
                        ? "nav-link active"
                        : "nav-link"
                    }
                    onClick={() =>
                    setActiveTab("ITEMS")
                    }
                >
                    Items
                </button>

                </li>

                <li className="nav-item">

                <button
                    type="button"
                    className={
                    activeTab === "CHECKLISTS"
                        ? "nav-link active"
                        : "nav-link"
                    }
                    onClick={() =>
                    setActiveTab("CHECKLISTS")
                    }
                >
                    Checklists
                </button>

                </li>

            </ul>

            </div>

            <div className="card-body">

            {activeTab === "ITEMS" && (

                <TemplateItemsTab
                templateId={id}
                />

            )}

            {activeTab === "CHECKLISTS" && (

                <TemplateChecklistsTab
                templateId={id}
                />

            )}

            </div>

        </div>

        )}

      {/* ==================================================== */}
      {/* FOOTER */}
      {/* ==================================================== */}

      <div className="card mt-3">

        <div className="card-body text-end">

          <button
            className="btn btn-primary me-2"
            onClick={handleSave}
            disabled={saving}
          >

            {saving
              ? "Saving..."
              : "Save"}

          </button>

          <button
            className="btn btn-secondary"
            onClick={() =>
              navigate(
                "/wms/templates"
              )
            }
          >
            Cancel
          </button>

        </div>

      </div>

    </div>
  );
}